import classNames from "classnames";
import {Suspense} from "react";
import {useFormContext} from "react-hook-form";

import {Alert} from "@/components/alert/Alert.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {ContactsSelectField} from "@/components/form/fields/ContactsSelectField/ContactsSelectField.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {TagsField} from "@/components/form/fields/TagsField/TagsField.tsx";
import {Section} from "@/components/section/Section.tsx";
import {ZENDESK_TAGS_ARTICLE} from "@/lib/constants/links.ts";
import {AutoLocationServiceTypes, ServiceField} from "@/lib/constants/service.ts";
import {ContactGroupItems} from "@/lib/queries/contacts.ts";
import {
  MonitoringServiceChoices,
  MonitoringServiceType,
  MSPInterval,
  ServiceDefinition,
  ServiceGroup,
  ServiceTag,
} from "@/lib/types/checks.ts";
import {MonitoringGroup, MonitoringGroupsArchMapperEntry} from "@/lib/types/monitoring.ts";
import {Snippets} from "@/lib/types/website.ts";
import {CheckFormAutoGeneratedField} from "@/pages/checks/components/form/fields/CheckFormAutoGeneratedField.tsx";
import {FieldType} from "@/pages/checks/components/form/fields/common.ts";
import {IntervalField} from "@/pages/checks/components/form/fields/interval/IntervalField.tsx";
import {getIntervalType, MAX_INTERVAL_EXT_TXN} from "@/pages/checks/components/form/fields/interval/utils.ts";
import {LocationsField} from "@/pages/checks/components/form/fields/LocationsField.tsx";
import {ServiceTypeField} from "@/pages/checks/components/form/fields/ServiceTypeField.tsx";
import {CheckSnippet} from "@/pages/checks/components/form/snippets/CheckSnippet.tsx";
import {FIELD_TYPES} from "@/pages/checks/constants.ts";
import {CheckThirdPartyForm} from "@/pages/checks/update/sections/CheckThirdPartyForm.tsx";
import {CheckThirdPartyFormv2} from "@/pages/checks/update/sections/CheckThirdPartyFormv2.tsx";
import {CheckGlobalPingForm} from "@/pages/checks/update/sections/globalping/CheckGlobalPingFormLazy.tsx";
import {hasCheckSteps, hasSnippet, isLocationFieldVisible} from "@/pages/checks/utils.ts";

import styles from "./CheckDetailsForm.module.scss";

interface CheckDetailsFormProps {
  service: ServiceDefinition;
  serviceType: MonitoringServiceType;
  setServiceType: (t: MonitoringServiceType) => void;
  monitoringGroups: MonitoringGroup[];
  monitoringGroupsArchMapper: MonitoringGroupsArchMapperEntry[];
  monitoringServiceTypes: ServiceGroup[];
  mspInterval: MSPInterval;
  mspChoices: MonitoringServiceChoices;
  contactGroups: ContactGroupItems;
  tags: ServiceTag[];
  serviceUUID?: string;
  snippets: Snippets;
  disableServiceTypeSelector?: boolean;
  testRunnerForm?: null | JSX.Element;
}

export const CheckDetailsForm = ({
  service,
  serviceType,
  setServiceType,
  monitoringGroups,
  monitoringGroupsArchMapper,
  monitoringServiceTypes,
  mspInterval,
  mspChoices,
  contactGroups,
  tags,
  serviceUUID,
  snippets,
  disableServiceTypeSelector,
  testRunnerForm,
}: CheckDetailsFormProps) => {
  const {watch, getFieldState} = useFormContext();
  const {user, userHasFeature} = useCurrentUserContext();
  const intervalSpec = service.fields.find((f) => f.field === ServiceField.INTERVAL);
  const serviceFieldsMap = service.fields.reduce(
    (acc, field) => {
      acc[field.field as ServiceField] = field;
      return acc;
    },
    {} as Record<ServiceField, (typeof service.fields)[number]>
  );
  const checkSupportsNagios = monitoringGroupsArchMapper.find(
    (entry) => entry.monitoring_service_type == serviceType
  )?.has_nagios;
  const isLocationVisible =
    checkSupportsNagios && isLocationFieldVisible(monitoringGroups, service.force_monitoring_group_type);
  const locations = watch(ServiceField.LOCATION) || {length: 0};
  const isAverageGroup =
    serviceType === MonitoringServiceType.GROUP && watch(ServiceField.GROUP_UPTIME_PERCENT_CALCULATION) === "AVERAGE";
  const checkId = watch(ServiceField.ID);
  const selectedContacts = watch(ServiceField.CONTACT_GROUPS) || {length: 0};
  const interval = watch(ServiceField.INTERVAL);
  const intervalState = getFieldState(ServiceField.INTERVAL);
  const threshold = watch(ServiceField.THRESHOLD);
  const isExtendedTransactionsEnabled =
    serviceType === MonitoringServiceType.TRANSACTION && user!.account.enable_transaction_ext_checks;
  const isExtendedTransaction = isExtendedTransactionsEnabled && threshold > 60;

  return (
    <div className="pulse-color-gray-900">
      <div className="d-flex gap-4">
        <div className="pulseMaxWidthXL w-100">
          {checkId && <input name={ServiceField.ID} value={checkId} readOnly={true} type="number" className="d-none" />}
          <div className="pulseMaxWidthL">
            <ServiceTypeField
              svc={service}
              monitoringServiceTypes={monitoringServiceTypes}
              serviceType={serviceType}
              setServiceType={setServiceType}
              disabled={disableServiceTypeSelector}
            />
          </div>
          {serviceType === MonitoringServiceType.CLOUDSTATUS ? (
            <>{userHasFeature("cloudstatus_v2") ? <CheckThirdPartyFormv2 /> : <CheckThirdPartyForm />}</>
          ) : (
            <InputField name={ServiceField.NAME} label="Name of Check" className="pulseMaxWidthL mt-4" />
          )}
          <IntervalField
            className="mt-4"
            name={ServiceField.INTERVAL}
            intervalLabel={intervalSpec?.verbose_name}
            limit_check_frequency={user!.account.limit_check_frequency}
            svcType={service.service_type}
            intervalFrequency={intervalSpec?.mapped_fields?.frequency}
            forceInterval={service.force_interval}
            mspInterval={mspInterval}
            fieldType={getIntervalType(service.force_interval)}
            forceMaxInterval={isExtendedTransactionsEnabled ? MAX_INTERVAL_EXT_TXN : undefined}
            lockedTooltip="Frequency is locked for this check type."
          />
          {serviceFieldsMap[ServiceField.ADDRESS] && serviceType != MonitoringServiceType.CLOUDSTATUS && (
            <>
              <div className="pulseMaxWidthL mt-4">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.ADDRESS]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FIELD_TYPES[ServiceField.ADDRESS]}
                  mspChoices={mspChoices}
                />
              </div>
              {[
                MonitoringServiceType.FTP,
                MonitoringServiceType.SFTP,
                MonitoringServiceType.NTP,
                MonitoringServiceType.SSH,
                MonitoringServiceType.TCP,
                MonitoringServiceType.UDP,
              ].includes(serviceType) && (
                <div className="mt-4 pulseMaxWidthXS">
                  <CheckFormAutoGeneratedField
                    field={serviceFieldsMap[ServiceField.PORT]}
                    versions={service.versions}
                    serviceType={service.service_type}
                    type={FIELD_TYPES[ServiceField.PORT]}
                    mspChoices={mspChoices}
                  />
                </div>
              )}
            </>
          )}
          {serviceType === MonitoringServiceType.RUM2 && (
            <>
              <div className="mt-4 pulseMaxWidthS">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.RUM_AGGREGATION_TYPE]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FIELD_TYPES[ServiceField.RUM_AGGREGATION_TYPE]}
                  mspChoices={mspChoices}
                  serviceTags={tags}
                />
              </div>
              <div className="mt-4 pulseMaxWidthM">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.RUM_APDEX_THRESHOLD]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FIELD_TYPES[ServiceField.RUM_APDEX_THRESHOLD]}
                  mspChoices={mspChoices}
                  serviceTags={tags}
                />
              </div>
            </>
          )}
          {serviceType === MonitoringServiceType.SSL_CERT && (
            <div className="mt-4 d-flex gap-4 gap-sm-3 flex-column flex-sm-row">
              <div className="pulseMaxWidthS flex-grow-1">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.THRESHOLD]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FIELD_TYPES[ServiceField.THRESHOLD]}
                  mspChoices={mspChoices}
                  serviceTags={tags}
                />
              </div>
              <div className="pulseMaxWidthS flex-grow-1">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.SSL_PROTOCOL]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FIELD_TYPES[ServiceField.SSL_PROTOCOL]}
                  mspChoices={mspChoices}
                  serviceTags={tags}
                />
              </div>
            </div>
          )}
          {serviceType === MonitoringServiceType.WHOIS && (
            <>
              <div className="mt-4 pulseMaxWidthS">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.THRESHOLD]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FIELD_TYPES[ServiceField.THRESHOLD]}
                  mspChoices={mspChoices}
                />
              </div>
              <div className="mt-4 pulseMaxWidthL">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.EXPECT_STRING]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FieldType.WHOISAutoload}
                  mspChoices={mspChoices}
                />
              </div>
            </>
          )}
          {serviceType === MonitoringServiceType.RDAP && (
            <>
              <div className="mt-4 pulseMaxWidthS">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.THRESHOLD]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FIELD_TYPES[ServiceField.THRESHOLD]}
                  mspChoices={mspChoices}
                />
              </div>
              <div className="mt-4 pulseMaxWidthXL">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.EXPECT_STRING]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FieldType.RDAPAutoload}
                  mspChoices={mspChoices}
                />
              </div>
            </>
          )}
          {serviceType === MonitoringServiceType.DNS && (
            <>
              <div className="mt-4 pulseMaxWidthXS">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.DNS_RECORD_TYPE]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FIELD_TYPES[ServiceField.DNS_RECORD_TYPE]}
                  mspChoices={mspChoices}
                />
              </div>
              <div className="pulseMaxWidthL mt-4">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.EXPECT_STRING]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FieldType.DNSAutoload}
                  mspChoices={mspChoices}
                />
              </div>
              <div className="pulseMaxWidthL mt-4">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.DNS_SERVER]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FIELD_TYPES[ServiceField.DNS_SERVER]}
                  mspChoices={mspChoices}
                />
              </div>
            </>
          )}
          {serviceType === MonitoringServiceType.NETWORK && (
            <Suspense fallback={null}>
              <CheckGlobalPingForm />
            </Suspense>
          )}
          {testRunnerForm && !hasCheckSteps(serviceType) && <div className={`d-md-none mt-4`}>{testRunnerForm}</div>}
          {isLocationVisible && (
            <div className="pulseMaxWidthL mt-4">
              <LocationsField
                svc={service}
                name={ServiceField.LOCATION}
                monitoringGroups={monitoringGroups}
                limit_monitoring_group_level={user!.account.limit_monitoring_group_level}
                limit_monitoring_groups={user!.account.limit_monitoring_groups}
                monitoringGroupsArchMapper={monitoringGroupsArchMapper}
              />
              {!AutoLocationServiceTypes.includes(serviceType) && !isExtendedTransaction && locations.length < 3 && (
                <Alert
                  callout="Attention"
                  variant="maintenance"
                  className="mb-0 mt-3"
                  children="We recommend using at least 3 locations per Check."
                />
              )}
              {serviceType === MonitoringServiceType.TRANSACTION &&
                !intervalState.invalid &&
                interval < 5 &&
                locations.length > 3 && (
                  <Alert
                    callout="Attention"
                    variant="maintenance"
                    className="mb-0 mt-3"
                    children="Micro Transaction Checks with an interval of less than 5 minutes should have 3 or fewer locations."
                  />
                )}
            </div>
          )}
          <Section
            title={"Notifications & Metadata"}
            description={"Control alert routing and check categorization"}
            className="pulseMaxWidthL mt-4 pt-2"
          />
          <div className="pulseMaxWidthL mt-4">
            <ContactsSelectField
              contacts={contactGroups.items}
              name={ServiceField.CONTACT_GROUPS}
              disabled={isAverageGroup}
              helpText={
                isAverageGroup
                  ? "Reporting on the group’s average uptime percentage will not issue alerts for downtime within this group check."
                  : ""
              }
            />
            {!isAverageGroup && selectedContacts.length < 1 && (
              <Alert
                callout="Attention"
                variant="maintenance"
                className="mb-0 mt-3"
                children="This Check will not issue Alert notifications until a contact is added. (Does not affect configured escalations)."
              />
            )}
          </div>
          <div className="pulseMaxWidthL mt-4">
            <TagsField
              name={ServiceField.TAGS}
              label="Tags"
              tooltip="Apply one or more Tags to this Check."
              helpText="Manage Check Tags."
              supportLink={ZENDESK_TAGS_ARTICLE}
              tags={tags}
            />
          </div>
          {serviceType === MonitoringServiceType.CLOUDSTATUS && (
            <SwitchField
              className="pulseMaxWidthL mt-4"
              variant="card"
              name="notify_only_on_down"
              label="Opt out of Maintenance notifications"
              helpText="Limit notifications to downtime events only"
            />
          )}
          {serviceUUID && hasSnippet(serviceType) && (
            <CheckSnippet uuid={serviceUUID} serviceType={serviceType} snippets={snippets} />
          )}
        </div>
        {testRunnerForm && !hasCheckSteps(serviceType) && (
          <>
            <div className="d-none d-md-block">
              <Divider orientation="vertical" className="mx-2" />
            </div>
            <div className={classNames(styles.testRunner, "d-none d-md-block")}>{testRunnerForm}</div>
          </>
        )}
      </div>
    </div>
  );
};
